import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScanText, Upload, FileText, CheckCircle2, Clock, AlertCircle, Download } from 'lucide-react';
import { cn, formatDateTime } from '@/lib/utils';

interface RemittanceDocument {
  id: string;
  fileName: string;
  uploadDate: string;
  status: 'processing' | 'completed' | 'failed';
  invoicesMatched: number;
  totalAmount: number;
  customerName?: string;
}

export default function OCRRemittancePage() {
  const [documents] = useState<RemittanceDocument[]>([
    {
      id: '1',
      fileName: 'remittance_2024_001.pdf',
      uploadDate: '2024-01-15T10:30:00',
      status: 'completed',
      invoicesMatched: 5,
      totalAmount: 15750.0,
      customerName: 'Acme Corporation',
    },
    {
      id: '2',
      fileName: 'payment_advice_jan.pdf',
      uploadDate: '2024-01-14T14:20:00',
      status: 'processing',
      invoicesMatched: 0,
      totalAmount: 0,
    },
    {
      id: '3',
      fileName: 'remittance_2024_002.pdf',
      uploadDate: '2024-01-13T09:15:00',
      status: 'completed',
      invoicesMatched: 3,
      totalAmount: 8920.5,
      customerName: 'Global Industries Ltd',
    },
  ]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="outline" className="border-green-500 text-green-500">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-500">
            <Clock className="mr-1 h-3 w-3" />
            Processing
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="outline" className="border-red-500 text-red-500">
            <AlertCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">OCR Remittance</h2>
          <p className="text-muted-foreground">
            Upload PDF remittances for automatic invoice matching using OCR technology
          </p>
        </div>
        <Button>
          <Upload className="mr-2 h-4 w-4" />
          Upload Remittance
        </Button>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Processed</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">24</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Invoices Matched</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">156</div>
            <p className="text-xs text-muted-foreground">Automatically matched</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <ScanText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(245670.5)}</div>
            <p className="text-xs text-muted-foreground">Processed this month</p>
          </CardContent>
        </Card>
      </div>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Remittance Document</CardTitle>
          <CardDescription>
            Upload PDF remittance advice documents. Our OCR system will automatically extract invoice numbers and
            amounts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Drop your PDF files here</h3>
            <p className="text-sm text-muted-foreground mb-4">or click to browse from your computer</p>
            <Button variant="outline">Select Files</Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Documents */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Documents</CardTitle>
          <CardDescription>View recently uploaded remittance documents and their processing status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead className="text-center">Invoices Matched</TableHead>
                  <TableHead className="text-right">Total Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No documents uploaded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          {doc.fileName}
                        </div>
                      </TableCell>
                      <TableCell>{doc.customerName || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell>{formatDateTime(new Date(doc.uploadDate))}</TableCell>
                      <TableCell className="text-center">
                        {doc.status === 'completed' ? (
                          <span className="font-medium">{doc.invoicesMatched}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {doc.status === 'completed' ? (
                          <span className="font-medium">{formatCurrency(doc.totalAmount)}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          {doc.status === 'completed' && (
                            <Button variant="ghost" size="sm">
                              View Details
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* How it Works */}
      <Card>
        <CardHeader>
          <CardTitle>How OCR Remittance Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <Upload className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">1. Upload Document</h3>
              <p className="text-sm text-muted-foreground">Upload your PDF remittance advice document</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <ScanText className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">2. OCR Processing</h3>
              <p className="text-sm text-muted-foreground">Our AI extracts invoice numbers and amounts automatically</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">3. Auto-Match</h3>
              <p className="text-sm text-muted-foreground">Invoices are automatically matched and allocated</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
